import { FormEvent, useMemo } from 'react';
import { Info, Loader2, Save, X } from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import TextAreaField from '../../../components/Forms/Input/TextAreaField';
import ToggleSwitch from '../../../components/Forms/Input/ToggleSwitch';
import FormModal from '../../../components/Modals/FormModal';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';
import { RestaurantRawMaterialCategoryDTO } from '../../../types/restaurant/RestaurantRawMaterialCategoryTypes';
import {
  RawMaterialSupplierOption,
  RawMaterialUnitOption,
  RestaurantRawMaterialForm,
} from '../../../types/restaurant/RestaurantRawMaterialTypes';

interface Props {
  isOpen: boolean;
  form: RestaurantRawMaterialForm;
  categories: RestaurantRawMaterialCategoryDTO[];
  units: RawMaterialUnitOption[];
  suppliers: RawMaterialSupplierOption[];
  saving: boolean;
  onClose: () => void;
  onChange: <K extends keyof RestaurantRawMaterialForm>(
    field: K,
    value: RestaurantRawMaterialForm[K]
  ) => void;
  onSave: () => void;
}

const RawMaterialFormModal = ({
  isOpen,
  form,
  categories,
  units,
  suppliers,
  saving,
  onClose,
  onChange,
  onSave,
}: Props) => {
  // =====================================================
  // 🔹 Validaciones
  // =====================================================

  const errors = useMemo(() => {
    const nextErrors: Record<string, string> = {};

    if (!form.categoryId) {
      nextErrors.categoryId = 'Seleccione una categoría.';
    }

    if (!form.unitId) {
      nextErrors.unitId = 'Seleccione una unidad.';
    }

    if (!form.code.trim()) {
      nextErrors.code = 'El código es obligatorio.';
    } else if (form.code.trim().length > 50) {
      nextErrors.code = 'Máximo 50 caracteres.';
    }

    if (!form.name.trim()) {
      nextErrors.name = 'El nombre es obligatorio.';
    } else if (form.name.trim().length > 150) {
      nextErrors.name = 'Máximo 150 caracteres.';
    }

    (
      ['minimumStock', 'currentStock', 'cost'] as const
    ).forEach((field) => {
      if (form[field] !== '' && Number(form[field]) < 0) {
        nextErrors[field] = 'No puede ser negativo.';
      }
    });

    if (form.description.length > 500) {
      nextErrors.description = 'Máximo 500 caracteres.';
    }

    return nextErrors;
  }, [form]);

  const hasErrors = Object.keys(errors).length > 0;

  // =====================================================
  // 🔹 Guardar
  // =====================================================

  const save = () => {
    if (saving || hasErrors) return;
    onSave();
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    save();
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title={form.id ? 'Editar materia prima' : 'Nueva materia prima'}
      maxWidth="max-w-4xl"
      headerExtra={<RestaurantFormHelp topic="rawMaterial" />}
    >
      <form onSubmit={submit} className="space-y-5">
        {/* =====================================================
            🔹 Información
        ===================================================== */}

        <div
          className="
            flex
            items-start
            gap-3

            rounded-xl

            border
            border-primary/15

            bg-primary/5

            p-4

            text-sm
            text-black

            dark:border-primary/25
            dark:bg-primary/10
            dark:text-white
          "
        >
          <span
            className="
              flex
              shrink-0
              items-center
              justify-center

              rounded-lg

              bg-primary/10

              p-2

              text-primary

              dark:bg-primary/15
            "
          >
            <Info className="h-5 w-5" />
          </span>

          <div>
            <p className="font-medium">
              Registro inicial de existencias
            </p>

            <p className="mt-1 text-body dark:text-bodydark2">
              El stock actual se registra aquí como valor inicial. Los
              movimientos posteriores deben realizarse desde el módulo de
              inventario.
            </p>
          </div>
        </div>

        {/* =====================================================
            🔹 Información general
        ===================================================== */}

        <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
          <SimpleSearchSelect
            label="Categoría"
            name="categoryId"
            selectedValue={form.categoryId || null}
            iconKey="category"
            required
            error={errors.categoryId}
            options={categories.map((category) => ({
              id: category.id,
              name: category.name,
            }))}
            onSelect={(option) =>
              onChange('categoryId', option.id)
            }
          />

          <SimpleSearchSelect
            label="Unidad de medida"
            name="unitId"
            selectedValue={form.unitId || null}
            iconKey="unitMeasure"
            required
            error={errors.unitId}
            options={units.map((unit) => ({
              id: unit.id,
              name: unit.name,
            }))}
            onSelect={(option) =>
              onChange('unitId', option.id)
            }
          />

          <SimpleSearchSelect
            label="Proveedor (opcional)"
            name="supplierId"
            selectedValue={
              form.supplierId === '' ? null : form.supplierId
            }
            iconKey="supplier"
            options={[
              {
                id: 0,
                name: 'Sin proveedor',
              },
              ...suppliers.map((supplier) => ({
                id: supplier.id,
                name: supplier.tradeName || supplier.name,
              })),
            ]}
            onSelect={(option) =>
              onChange(
                'supplierId',
                option.id === 0 ? '' : option.id
              )
            }
          />

          <InputField
            label="Código"
            name="code"
            type="text"
            iconKey="code"
            value={form.code}
            required
            error={errors.code}
            placeholder="Ej. MP-001"
            onChange={(event) =>
              onChange(
                'code',
                event.target.value.toUpperCase()
              )
            }
          />

          <InputField
            label="Nombre"
            name="name"
            type="text"
            iconKey="product"
            value={form.name}
            required
            error={errors.name}
            placeholder="Ej. Harina de trigo"
            onChange={(event) =>
              onChange('name', event.target.value)
            }
          />

          <InputField
            label="Código de barras"
            name="barcode"
            type="text"
            iconKey="barcode"
            value={form.barcode}
            placeholder="Código de barras opcional"
            onChange={(event) =>
              onChange('barcode', event.target.value)
            }
          />

          <InputField
            label="Stock actual"
            name="currentStock"
            type="number"
            iconKey="stock"
            value={form.currentStock}
            min={0}
            step="0.000001"
            error={errors.currentStock}
            placeholder="0"
            onChange={(event) =>
              onChange(
                'currentStock',
                event.target.value === ''
                  ? ''
                  : Number(event.target.value)
              )
            }
          />

          <InputField
            label="Stock mínimo"
            name="minimumStock"
            type="number"
            iconKey="warning"
            value={form.minimumStock}
            min={0}
            step="0.000001"
            error={errors.minimumStock}
            placeholder="0"
            onChange={(event) =>
              onChange(
                'minimumStock',
                event.target.value === ''
                  ? ''
                  : Number(event.target.value)
              )
            }
          />

          <InputField
            label="Costo unitario"
            name="cost"
            type="number"
            iconKey="dollar"
            value={form.cost}
            min={0}
            step="0.000001"
            error={errors.cost}
            placeholder="0.00"
            onChange={(event) =>
              onChange(
                'cost',
                event.target.value === ''
                  ? ''
                  : Number(event.target.value)
              )
            }
          />

          {form.id && (
            <div
              className="
                mb-5

                flex
                items-center

                rounded-xl

                border
                border-stroke

                bg-gray-2/40

                px-4
                py-3

                dark:border-strokedark
                dark:bg-meta-4/30
              "
            >
              <ToggleSwitch
                label="Registro activo"
                name="rawMaterialActive"
                checked={form.active ?? true}
                onChange={() =>
                  onChange(
                    'active',
                    !(form.active ?? true)
                  )
                }
              />
            </div>
          )}
        </div>

        {/* =====================================================
            🔹 Descripción
        ===================================================== */}

        <TextAreaField
          label="Descripción"
          name="description"
          value={form.description}
          rows={4}
          error={errors.description}
          placeholder="Agregue una descripción de la materia prima..."
          helperText="Puede incluir presentación, características o indicaciones de almacenamiento."
          rightContent={
            <span
              className={
                form.description.length >= 500
                  ? 'text-danger'
                  : 'text-bodydark2'
              }
            >
              {form.description.length}/500
            </span>
          }
          onChange={(event) =>
            onChange('description', event.target.value)
          }
        />

        {/* =====================================================
            🔹 Acciones
        ===================================================== */}

        <div
          className="
            flex
            flex-col-reverse
            gap-3

            border-t
            border-stroke

            pt-5

            dark:border-strokedark

            sm:flex-row
            sm:justify-end
          "
        >
          <ActionButton
            label="Cancelar"
            icon={<X className="h-4 w-4" />}
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled={saving}
            onClick={onClose}
            className="w-full sm:w-auto"
          />

          <ActionButton
            label={
              saving
                ? 'Guardando'
                : form.id
                ? 'Guardar cambios'
                : 'Guardar materia prima'
            }
            icon={
              saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )
            }
            variant="primary"
            size="md"
            fullWidth={false}
            disabled={saving || hasErrors}
            onClick={save}
            className="w-full whitespace-nowrap sm:w-auto"
          />

          {/*
            ActionButton usa type="button". Este botón oculto mantiene
            el envío del formulario al presionar Enter desde un campo.
          */}
          <button
            type="submit"
            tabIndex={-1}
            aria-hidden="true"
            className="hidden"
          />
        </div>
      </form>
    </FormModal>
  );
};

export default RawMaterialFormModal;